import React from "react";
import { Button } from "@/components/ui/button";
import { TickIcon } from "@/components/Icons/TickIcon";
import { EditIcon } from "@/components/Icons/EditIcon";
import { cn } from "@/lib/utils";

export type ClusterFormStepProps = {
  id: string;
  icon: React.ReactNode;
  title: string;
  description?: string;
  component: React.ReactNode;
  completedTitle?: string;
  completedComponent?: React.ReactNode;
  /** When true, always render `component` instead of the collapsed completed summary. */
  alwaysExpanded?: boolean;
  state: "current" | "completed" | "next";
  onEdit?: () => void;
  onNext?: () => void;
  secondaryAction?: React.ReactNode;
  className?: string;
  isNextDisabled?: boolean;
};

export const ClusterFormStep: React.FC<ClusterFormStepProps> = ({
  id,
  icon,
  title,
  description,
  component,
  completedTitle,
  completedComponent,
  alwaysExpanded = false,
  state,
  isNextDisabled,
  className,
  onEdit,
  onNext,
  secondaryAction
}) => {
  const isCompleted = state === "completed";


  if (isCompleted && !completedComponent && !alwaysExpanded) {
    return null;
  }

  const showExpandedContent = !isCompleted || alwaysExpanded;
  return (
    <section
      id={id}
      className={cn(`flex flex-row gap-4 max-lg:flex-col max-lg:gap-2 ${className || ""}`)}
    >

      {/* Right content */}
      {showExpandedContent ? (
        <div key="current-content" className="flex flex-1 flex-col gap-4 max-lg:gap-4">
          {((title || description) || (onNext || secondaryAction)) && <div className="flex items-center justify-between max-lg:flex-col max-lg:items-start max-lg:gap-2">
            {(title || description) && (
              <div className="flex flex-col gap-1">
                {title ? (
                  <h3 className="max-lg:subtext-medium text-2xl font-medium text-foreground max-lg:w-full max-lg:leading-5">
                    {title}
                  </h3>
                ) : null}
                {description ? (
                  <p className="subtext-medium max-lg:subtext-x-small max-w-[930px] leading-6 text-gray-light-1700 max-lg:leading-4">
                    {description}
                  </p>
                ) : null}
              </div>
            )}
            {(onNext || secondaryAction) && (
              <div className="flex items-center gap-2 max-lg:hidden">
                {secondaryAction}
                {onNext ? (
                  <Button
                    variant="primary"
                    className="label-medium rounded-[6px] px-3 py-[10px] font-medium leading-5 transition-[transform,opacity]"
                    onClick={onNext}
                    disabled={isNextDisabled}
                  >
                    Next Step
                  </Button>
                ) : null}
              </div>
            )}
          </div>}
          <div className="flex flex-col gap-4">{component}</div>
        </div>
      ) : (
        <>
          <div key="completed-content" className="flex flex-1 flex-col gap-4 lg:max-w-[600px]">
            {completedTitle && (
              <div className="flex items-center justify-between gap-4 lg:pt-[6px]">
                <div
                  className={
                    "inline-flex h-[48px] w-[48px] flex-shrink-0 items-center justify-center rounded-full border border-blue-dark-600 bg-container transition-colors dark:bg-[#001B3A] max-lg:h-[32px] max-lg:w-[32px] lg:hidden"
                  }
                >
                  <TickIcon className="h-6 w-6 text-foreground max-lg:h-4 max-lg:w-4" />
                </div>
                <h3
                  className={cn(
                    "label-medium font-medium leading-6 text-foreground",
                    isCompleted && "max-lg:mr-auto"
                  )}
                >
                  {completedTitle}
                </h3>
                {onEdit ? (
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-0 py-1 hover:bg-transparent hover:text-gray-dark-1000"
                    onClick={onEdit}
                  >
                    <EditIcon className="h-4 w-4" />
                    <span className="label-medium font-medium leading-6">Edit</span>
                  </Button>
                ) : null}
              </div>
            )}
            <div className="flex flex-col gap-4">{completedComponent}</div>
          </div>
        </>
      )}
    </section>
  );
};
